import axios from 'axios'
import { loginRequest } from './authConfig'
import { showApiErrorToast } from '../../utils/toast'
import { clearAllCache } from '../../utils/cacheUtils'
import { getDeviceSessionToken, clearDeviceSession, publishGateCode, isGateCode, GATE_CODES } from './deviceSession'

// The device endpoints are where a browser proves possession; a 401 from
// /v1/device/assert/verify (bad signature, WEBAUTHN_ORIGINS mismatch) is an
// assertion failure for DeviceGate to render, not an expired login.
const isDeviceUrl = url => typeof url === 'string' && /^(?:https?:\/\/[^/]+)?(?:\/api)?\/v1\/device\//.test(url)

// A 403 on a blob/arraybuffer request carries its JSON body as bytes. Best
// effort: decode it so the gate code is still visible. Never throws.
const readErrorBody = async response => {
  const data = response?.data
  try {
    if (typeof Blob !== 'undefined' && data instanceof Blob) return JSON.parse(await data.text())
    if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
      return JSON.parse(new TextDecoder().decode(data))
    }
  } catch (parseError) {
    return data
  }
  return data
}

const addJwtInterceptor = (instance) => {

  // Request interceptor for adding JWT token
  axios.interceptors.request.use(async (config) => {
    const accounts = instance.getAllAccounts()
    const account = accounts[0]

    if (!account) {
      throw Error('No active account! Verify a user has been signed in.')
    }

    // Device passkey gate: the session the API minted after a WebAuthn
    // assertion rides next to the bearer. Absent when there is none — the API
    // then answers DEVICE_REQUIRED (enforce) or admits (off / report).
    const deviceSession = getDeviceSessionToken()
    if (deviceSession) {
      config.headers['X-FMS-Device'] = deviceSession
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account
      });
      const bearer = `Bearer ${response.accessToken}`
      config.headers.Authorization = bearer
    } catch (error) {
      // Handle expired refresh token
      console.log('Token acquisition failed, clearing cache and redirecting to login:', error);
      try {
        // Clear all cache before redirecting (same as logout logic)
        await clearAllCache();
      } catch (cacheError) {
        console.error('Failed to clear cache during token refresh:', cacheError);
      }
      instance.loginRedirect();
    }

    return config
  })

  // Response interceptor for handling errors globally
  axios.interceptors.response.use(
    // Success response handler
    (response) => {
      return response
    },
    // Error response handler
    async (error) => {
      // A 403 carrying one of the device-gate codes is an account/device
      // state, not an error: no toast, no login redirect, no cache wipe. The
      // code goes to <DeviceGate>, which renders the matching access screen.
      // Only a revoked or foreign session is dropped here; DEVICE_REQUIRED
      // and DEVICE_PENDING leave storage alone so the gate decides.
      const gateCode = error.response?.status === 403 ? (await readErrorBody(error.response))?.code : null
      if (isGateCode(gateCode)) {
        if (gateCode === GATE_CODES.DEVICE_REVOKED || gateCode === GATE_CODES.DEVICE_MISMATCH) {
          clearDeviceSession()
        }
        publishGateCode(gateCode)
        return Promise.reject(error)
      }

      // Only show toast for errors that aren't handled locally
      // You can add a flag to config to skip global error handling
      if (!error.config?.skipGlobalErrorHandler) {
        showApiErrorToast(error)
      }

      // If it's a 401 and we have an instance, clear cache and redirect to login
      if (error.response?.status === 401 && instance && !isDeviceUrl(error.config?.url)) {
        console.log('401 Unauthorized response, clearing cache and redirecting to login');
        try {
          // Clear all cache before redirecting (same as logout logic)
          await clearAllCache();
        } catch (cacheError) {
          console.error('Failed to clear cache during 401 handling:', cacheError);
        }
        instance.loginRedirect();
      }

      return Promise.reject(error)
    }
  )
}

export default addJwtInterceptor
